import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Box, CustomTheme, Drawer, List, useTheme } from '@mui/material';
import { MenuItem } from './components/MenuItem';
import { SubMenu } from './components/SubMenu';
import { ISubMenuOption } from './components/SubMenu/interfaces';
import { useLocation, useNavigate } from 'react-router';
import { IOption, ISideBar } from './interfaces';
import { getMenuOptionsBasedOnLocation } from './helpers';
import { ModuleSwitcher } from './components/ModuleSwitcher';
import { BackToPortalMenuItem } from './components/BackToPortalMenuItem';
import { useInDevMode } from '@hooks/useInDevMode';
import { usePermission } from '@hooks/usePermission';
import useMediaQuery from '@mui/material/useMediaQuery';

export const SideBar: React.FC<ISideBar> = ({ open, onClose }): JSX.Element => {
  const theme = useTheme<CustomTheme>();
  const maxWidth = parseInt(theme.portal.maxWidth.replace('px', ''));
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 1000px)');
  const canDisplayManagementScreens = useMediaQuery('(min-width: 600px)');
  const inDevMode = useInDevMode();
  const [drawerX, setDrawerX] = useState<number>(0);
  const { can, hasAccessToModules } = usePermission();

  const showModuleSwitcher = hasAccessToModules && canDisplayManagementScreens;
  const showBackToPortalLink =
    !showModuleSwitcher && location.pathname.startsWith('/profile');

  const navigateTo = (
    mainMenuOption: IOption,
    subMenuOption?: ISubMenuOption
  ) => {
    if ((!subMenuOption && !mainMenuOption.subItems) || subMenuOption) {
      onClose();
    }

    navigate(subMenuOption?.link || mainMenuOption.link || '/');
  };

  const pathMatchesLink = (pathname: string, link: string): boolean => {
    if (link === '/') {
      return pathname === '/';
    }

    return pathname === link || pathname.startsWith(`${link}/`);
  };

  const filterOnPermissions = (options: IOption[]): IOption[] => {
    return options.filter((option) => {
      if (!option.permission) {
        return true;
      }

      return can(option.permission);
    });
  };

  const menuOptions = useMemo(() => {
    const options = getMenuOptionsBasedOnLocation(location, inDevMode);

    return filterOnPermissions(options);
  }, [location, inDevMode]);

  const isMenuOptionActive = (menuOptionLink: string): boolean => {
    if (!pathMatchesLink(location.pathname, menuOptionLink)) {
      return false;
    }

    const hasMoreSpecificMatch = menuOptions.some((option) => {
      if (!option.link || option.link === menuOptionLink) {
        return false;
      }

      return (
        option.link.length > menuOptionLink.length &&
        pathMatchesLink(location.pathname, option.link)
      );
    });

    return !hasMoreSpecificMatch;
  };

  const updatePosition = () => {
    const screenWidth = window.innerWidth;
    const leftOffset =
      screenWidth > maxWidth ? (screenWidth - maxWidth) / 2 : 0;
    setDrawerX(leftOffset);
  };

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);

    return () => window.removeEventListener('resize', updatePosition);
  }, []);

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={!isMobile || open}
      onClose={onClose}
    >
      <Box
        sx={{
          mt: '10px',
          ml: `${drawerX}px`,
          pt: '71px',
          width: theme.portal.sidebar.width,
        }}
      >
        <List>
          {showModuleSwitcher && <ModuleSwitcher onOptionSelected={onClose} />}
          {showBackToPortalLink && (
            <BackToPortalMenuItem onNavigate={onClose} />
          )}
          {menuOptions.map((option, index) => {
            return (
              <Fragment key={index}>
                <MenuItem
                  key={option.key}
                  label={option.label}
                  active={option.link ? isMenuOptionActive(option.link) : false}
                  onClick={() => navigateTo(option)}
                  icon={option.icon}
                />
                {option.subItems && (
                  <SubMenu
                    show={option.link ? isMenuOptionActive(option.link) : false}
                    menuOptions={option.subItems}
                    onSubItemClick={(subItem: ISubMenuOption) =>
                      navigateTo(option, subItem)
                    }
                  />
                )}
              </Fragment>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
};
